import { current } from '@reduxjs/toolkit'

import { initialState } from './importCSV'

import {
  generateSubscribersFieldErrors,
  mappedChangedHeaders,
  reverseMappedHeaders,
} from './helpers'
import { STEP_PROGRESS_TWO } from 'app/constants/subscriberCRM'
import { IMPORT_PROCESS_TYPE } from 'app/main/subscribersV4/AddSubscibers/components/newImport/constansts'
import { checkHeadersRequiredDependentOnEachOther } from 'app/main/subscribersV4/AddSubscibers/components/newImport/ImportFromCsv/StepOne/helpers'
import { clearCSVData } from './importCSVExtraReducers'

export const resetCSVFileData = (state) => {
  clearCSVData()
  return {
    ...initialState,
    optionsLanguages: state.optionsLanguages,
  }
}

export const setCSVFileData = (state, actions) => {
  const { data, activeStep, showUploadingProgressBar, showProgressBar } =
    actions.payload
  //show warnning column that is not align with csv template
  return {
    ...state,
    showProgressBar: showProgressBar ?? false,
    showUploadingProgressBar: showUploadingProgressBar ?? false,
    data: { ...state.data, ...data },
    activeStep,
  }
}

export const setOptionsLanguages = (state, actions) => {
  state.optionsLanguages = actions.payload
}

export const changeCSVData = (state, actions) => {
  const {
    data: { rows, headers },
  } = current(state)

  const {
    changedSubscriber,
    index,
    isUpdatedFromModal = false,
  } = actions.payload

  const updatedData = isUpdatedFromModal
    ? reverseMappedHeaders({
        rows: [changedSubscriber],
        headers,
        oldRows: [rows[index]],
      })[0]
    : changedSubscriber

  const newRows = [...rows]
  newRows[index] = updatedData

  state.data = {
    ...state.data,
    rows: newRows,
  }
}

export const changeHeader = (state, actions) => {
  const {
    data: { headers, infoMappedHeaders },
  } = current(state)
  const { newValue, oldValue, label, index } = actions.payload

  const newHeaders = headers.map((header, i) => {
    const { headerKey, changedHeaderkey } = header

    if (
      ((headerKey === newValue && !changedHeaderkey) ||
        changedHeaderkey === newValue) &&
      i !== index
    ) {
      return {
        ...header,
        label: 'subscribers.import.previewTable.dragAndDrop.notDefined.text',
        changedHeaderkey: '',
        isInvalid: true,
      }
    }

    if (i === index) {
      return {
        ...header,
        label,
        changedHeaderkey: headerKey === newValue ? '' : newValue,
        isInvalid: false,
      }
    }

    return header
  })

  const validHeaderKeys = new Set(
    newHeaders
      .filter(({ isInvalid }) => !isInvalid)
      .map(({ changedHeaderkey, headerKey }) => changedHeaderkey || headerKey),
  )

  const newInfoMappedHeaders = infoMappedHeaders.map((header) => {
    const { headerKey, required } = header
    const isRequired = checkHeadersRequiredDependentOnEachOther({
      required,
      header: headerKey,
      headerKeys: Array.from(validHeaderKeys),
    })

    if (headerKey === newValue) {
      return { ...header, isInvalid: false, required: isRequired }
    }

    if (headerKey === oldValue && !validHeaderKeys.has(oldValue)) {
      return { ...header, isInvalid: true, required: isRequired }
    }

    return { ...header, required: isRequired }
  })

  state.activeStep = STEP_PROGRESS_TWO
  state.data.validationInfo = initialState.data.validationInfo
  state.data.headers = newHeaders
  state.data.infoMappedHeaders = newInfoMappedHeaders
}

export const removeCSVDataRow = (state, actions) => {
  const {
    data: { rows, validationInfo },
  } = current(state)
  const { index } = actions.payload
  const { failedSubscribers } = validationInfo
  const newRows = rows.slice()
  newRows.splice(index, 1)
  state.data.rows = newRows
  state.data.validationInfo.failedSubscribers = failedSubscribers.filter(
    ({ id }) => index !== id,
  )
}

export function updateFailedSubscribers(
  type,
  importedSubscribersIds,
  previousFailedSubscribers,
  failedSubscribers,
) {
  let newFailedSubscribers = []
  if (type === IMPORT_PROCESS_TYPE) {
    const failedSubscribersMap = new Map(
      failedSubscribers.map((subscriber) => [subscriber.id, subscriber]),
    )
    newFailedSubscribers = previousFailedSubscribers
      .map((error) => {
        const matchingError = failedSubscribersMap.get(error.id)

        if (matchingError) {
          return { ...error, ...matchingError }
        }

        if (importedSubscribersIds.includes(error.id)) {
          return null
        }

        return error
      })
      .filter(Boolean)
  } else {
    newFailedSubscribers = [...previousFailedSubscribers, ...failedSubscribers]
  }
  return newFailedSubscribers
}

export const setValidationInfo = (state, actions) => {
  const {
    percent: newPercent,
    type,
    failedSubscribers,
    chunkCount,
    importId,
  } = actions.payload

  const {
    data: { validationInfo, importedSubscribersIds },
  } = current(state)
  let percent = validationInfo.percent
  const newValidatedChunkCount = validationInfo.validatedChunkCount + 1

  if (percent === undefined || percent < newPercent) {
    percent = newPercent > 100 ? 100 : newPercent
  }

  const newValidationInfo = {
    ...validationInfo,
    percent,
    type,
    chunkCount,
    importId,
    validatedChunkCount: newValidatedChunkCount,
    failedSubscribers: updateFailedSubscribers(
      type,
      importedSubscribersIds,
      validationInfo.failedSubscribers,
      failedSubscribers,
    ),
  }
  state.showProgressBar = percent < 100
  state.data.validationInfo = newValidationInfo
  state.data.importedSubscribersIds = []
}

export const resetValidationInfo = (state) => {
  const {
    data: { validationInfo },
  } = current(state)
  state.data.validationInfo = {
    ...initialState.data.validationInfo,
    failedSubscribers: validationInfo.failedSubscribers,
    previousProcessId: validationInfo.importId,
  }
}

export const mappedFailedSubscribers = (state) => {
  const {
    data: { rows, validationInfo },
  } = current(state)
  const { failedSubscribers, type } = validationInfo
  let newRows = generateSubscribersFieldErrors(rows, failedSubscribers)
  if (type === IMPORT_PROCESS_TYPE) {
    newRows = newRows.filter(({ errors }) => errors)
    state.data.importedSubscribersCount = Math.max(
      rows.length - newRows.length,
      0,
    )
    state.isOpenImportedSubscribersDialog = true
    state.data.sendInvitation = false
  }
  state.data.rows = newRows
  state.isSocketConnected = false
}

export const setActiveStep = (state, actions) => {
  state.activeStep = actions.payload
}

export const setShowProgressBar = (state, actions) => {
  state.showProgressBar = actions.payload
}

export const setDialogEditCSVDataRow = (state, actions) => {
  const { index, open } = actions.payload
  const {
    data: { rows, headers },
  } = current(state)

  let editableRow = {}
  if (open) {
    const [row] = mappedChangedHeaders({
      rows: [rows[index] || {}],
      headers,
    })
    editableRow = row
  }

  state.dialogEditCSVData = { open, data: editableRow, index }
}

export const setSendInvitation = (state, actions) => {
  state.data.sendInvitation = actions.payload
}

export const setTags = (state, actions) => {
  state.data.tags = actions.payload || []
}

export const setCancelImportDialog = (state, actions) => {
  state.cancelProcessDialog = actions.payload
}

export const setIsOpenDeleteCSVFileDialog = (state, actions) => {
  state.isOpenDeleteCSVFileDialog = actions.payload
}

export const setIsOpenImportedSubscribersDialog = (state, actions) => {
  state.isOpenImportedSubscribersDialog = actions.payload
}

export const setIsSocketConnected = (state, actions) => {
  state.isSocketConnected = actions.payload
}

export const setImportedSubscribersIds = (state, actions) => {
  state.data.importedSubscribersIds = actions.payload
}

export const setInitilization = (state, actions) => {
  state.activeStep = STEP_PROGRESS_TWO
  state.showUploadingProgressBar = true
}

export const setUploadingProgress = (state, actions) => {
  state.data.validationInfo.uploadingPercent = actions.payload * 100
}

export const setUploadingEnd = (state, actions) => {
  state.showUploadingProgressBar = false
  state.showProgressBar = true
  state.uploadingAxiosController = null
}

export const setUploadingStart = (state, actions) => {
  const controller = actions.payload
  if (controller) {
    state.uploadingAxiosController = controller
  }
}

export const setValidatingStart = (state, actions) => {
  const controller = actions.payload
  if (controller) {
    state.validatingAxiosController = controller
  }
}

export const setImportingStart = (state, actions) => {
  const controller = actions.payload
  if (controller) {
    state.importingAxiosController = controller
  }
}

export const setImportingEnd = (state, actions) => {
  state.importingAxiosController = null
}

export const setValidationErrorMessage = (state, actions) => {
  state.validationErrorMessage = actions.payload
}
